import { sessionState } from 'src/app/core/services/signal-state.storage';
import { DOCUMENT } from '@angular/common';
import { DestroyRef, Inject, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Component, HostListener, NgZone, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { delay } from 'rxjs/operators';
import * as e from 'express';
import { AppService } from './app.service';
import { Theme } from './shared/Models/Models';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  public isLeftMenuVisible: boolean = false;
  public isWebView: boolean = true;
  loader: boolean = false;
  sessionLoaded: boolean = false
  theme: Theme = 'light-theme';
  private pageshowHandler: ((e: PageTransitionEvent) => void) | null = null;

  constructor(
    private _appService: AppService,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    private router: Router,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.redirectToHomeAfterRefresh();
    this.initializeTheme();
    this._appService.setIsGdoPortal(false);
    if (this.isIosWebContext()) {
      this.document.body.classList.add('dpi1527-ios-webview');
    }
    this.setupiOSRepaintListeners();
    this.setVhProperty();
  }

  private redirectToHomeAfterRefresh(): void {
    const navigationEntry = window.performance?.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const isBrowserRefresh = navigationEntry?.type === 'reload' ||
      (!navigationEntry && window.performance?.navigation?.type === 1);

    if (isBrowserRefresh && this.router.url !== '/subscriptionWV') {
      void this.router.navigate(['/subscriptionWV'], { replaceUrl: true });
    }
  }

  private isIosWebContext(): boolean {
    return (
      sessionState.getItem('systemType') === 'ios' ||
      /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  ngOnDestroy(): void {
    if (this.pageshowHandler) {
      window.removeEventListener('pageshow', this.pageshowHandler as EventListener);
    }
  }

  /**
   * DPI-1527: iOS WebKit can suspend the Web Content process when the app
   * is backgrounded or the page loses focus. When it resumes, the GPU
   * compositor may not repaint the page, leaving a blank/clipped screen.
   * Forcing a layout recalculation on visibilitychange and pageshow events
   * works around this iOS WebKit rendering bug.
   */
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (!this.document.hidden) {
      this.forceRepaint();
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setVhProperty();
  }

  private setupiOSRepaintListeners(): void {
    this.pageshowHandler = (event: PageTransitionEvent) => {
      if (event.persisted) {
        this.ngZone.run(() => this.forceRepaint());
      }
    };
    window.addEventListener('pageshow', this.pageshowHandler as EventListener);
  }

  private setVhProperty(): void {
    const h = window.visualViewport?.height ?? window.innerHeight;
    this.document.documentElement.style.setProperty('--app-height', `${h}px`, 'important');
    this.document.documentElement.style.setProperty('--vh', `${h * 0.01}px`, 'important');
  }

  private forceRepaint(): void {
    this.setVhProperty();
    if (this.document.body.classList.contains('dpi1527-ios-webview')) {
      this.document.body.style.setProperty('overflow-y', 'auto', 'important');
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      return;
    }

    const h = window.visualViewport?.height ?? window.innerHeight;
    [this.document.documentElement, this.document.body].forEach(el => {
      if (!el) return;
      el.style.removeProperty('height');
      el.style.setProperty('min-height', `${h}px`, 'important');
      el.style.setProperty('overflow-y', 'auto', 'important');
    });

    document.querySelectorAll('*').forEach(el => {
      const htmlEl = el as HTMLElement;
      const cs = window.getComputedStyle(htmlEl);
      if (
        htmlEl.scrollHeight > htmlEl.clientHeight + 20 &&
        (cs.overflow === 'hidden' || cs.overflowY === 'hidden')
      ) {
        htmlEl.style.setProperty('overflow', 'visible', 'important');
        htmlEl.style.setProperty('overflow-y', 'visible', 'important');
      }
    });

    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  }

  ngAfterViewInit() {
    this._appService.getLeftMenuVisibilityStatus().pipe(delay(1), takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      this.isLeftMenuVisible = res;
    });
    this._appService.getWebViewStatus().pipe(delay(1), takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      this.isWebView = res;
      this.isLeftMenuVisible = !res
    });
  }
  initializeTheme = (): void => {
    this.renderer.addClass(this.document.body, this.theme);
  };
}
